import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { createStripeCustomer, attachPaymentMethodToCustomer, setDefaultPaymentMethod } from '../../../../../../Utilities/api';
import { WhiteBackButton } from '../../../../../../Utilities/components/Buttons';
import { StateContext } from '../../../../../../StateProvider';
import './AddPaymentMethod.css';
import Footer from '../../../../../../Footer';

const AddPaymentMethod = () => {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const { user, refreshUserData, loading, setLoading } = useContext(StateContext);
  const [cardholderName, setCardholderName] = useState('');
  const [error, setError] = useState(null);
  const [isDefault, setIsDefault] = useState(false);

  // ---------- Error message helpers (only addition) ----------
  function formatStripeError(err) {
    // Safe default
    var message = 'We couldn’t add your card. Please check the details and try again.';
    if (!err) return message;

    var type = err.type || '';
    var code = err.code || '';
    var decline = err.decline_code || '';
    var msg = err.message || '';

    // Prefer Stripe’s own clear message
    if (msg && msg.trim().length > 0) message = msg;

    if (type === 'card_error') {
      // Input/validation issues
      if (code === 'incomplete_number') message = 'Card number is incomplete.';
      else if (code === 'incomplete_cvc') message = 'CVC is incomplete.';
      else if (code === 'incomplete_expiry') message = 'Expiry date is incomplete.';
      else if (code === 'invalid_expiry_year' || code === 'invalid_expiry_month') message = 'The expiry date looks invalid.';
      else if (code === 'incorrect_number' || code === 'invalid_number') message = 'The card number looks invalid.';
      else if (code === 'incorrect_cvc') message = 'The CVC code looks incorrect.';
      else if (code === 'expired_card') message = 'This card is expired. Try a different card.';
      else if (code === 'postal_code_invalid') message = 'The postal/ZIP code looks invalid.';
      // Declines
      else if (code === 'card_declined') {
        if (decline === 'insufficient_funds') message = 'The card was declined due to insufficient funds.';
        else if (decline === 'do_not_honor') message = 'The bank declined this card. Try another card or contact your bank.';
        else if (decline === 'generic_decline') message = 'The bank declined this card. Try another card.';
        else if (decline === 'lost_card') message = 'This card has been reported lost. Please use a different card.';
        else if (decline === 'stolen_card') message = 'This card has been reported stolen. Please use a different card.';
        else if (decline === 'fraudulent') message = 'The bank flagged this as suspicious. Try another card or contact your bank.';
        else if (decline === 'restricted_card') message = 'This card can’t be used for this type of payment.';
        else if (decline === 'issuer_not_available') message = 'We couldn’t reach the card issuer. Please try again shortly.';
        else if (decline === 'processing_error') message = 'There was a processing error. Please try again in a moment.';
        else message = 'The card was declined. Try another card or contact your bank.';
      }
      // Processor hiccups
      else if (code === 'processing_error') message = 'There was a processing error. Please try again in a moment.';
    } else if (type === 'validation_error') {
      message = 'Some card details are missing or invalid. Please complete all fields.';
    } else if (type === 'api_connection_error') {
      message = 'We can’t reach the payment network. Check your internet and try again.';
    } else if (type === 'api_error' || type === 'rate_limit_error') {
      message = 'The payment service is busy. Please try again shortly.';
    } else if (type === 'authentication_error') {
      message = 'Your session needs to be refreshed. Please sign in again.';
    } else if (code === 'authentication_required') {
      message = 'Please authenticate this card with your bank and try again.';
    }

    return message;
  }

  function formatServerError(err, fallback) {
    var msg = fallback || 'A server error occurred. Please try again.';
    try {
      if (err && err.response) {
        var data = err.response.data;
        var nested =
          (data && data.message) ||
          (data && data.error && (data.error.message || data.error)) ||
          (Array.isArray(data && data.errors) && data.errors.length > 0 && (data.errors[0].message || data.errors[0])) ||
          null;

        if (nested && typeof nested === 'string') msg = nested;
        else if (typeof data === 'string' && data.trim().length > 0) msg = data;
      } else if (err && err.message) {
        msg = err.message;
      }
    } catch (_) {
      // keep fallback
    }
    return msg;
  }
  // ----------------------------------------------------------

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px', // 👈 Larger font size
        color: '#424770',
        padding: '15px',
        '::placeholder': { // Added placeholder styling
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  const handleAddPaymentMethod = async (e) => {
    e.preventDefault();
    setError(null); // Clear any previous error

    if (!stripe || !elements) {
      setError('Payment fields are still loading. Please wait a moment and try again.');
      return;
    }

    setLoading(true);

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Payment field did not initialize. Refresh the page and try again.');
      setLoading(false);
      return;
    }

    try {
      const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: cardholderName,
        },
      });

      if (pmError) {
        setError(formatStripeError(pmError));
        setLoading(false);
        return;
      }

      if (!paymentMethod) {
        setError('We could not create a payment method. Please try again.');
        setLoading(false);
        return;
      }

      // Create a Stripe customer if not already created
      let stripeCustomerId = user.stripeCustomerId;
      if (!stripeCustomerId) {
        try {
          const customerResponse = await createStripeCustomer(user.uid, user.email);
          stripeCustomerId = customerResponse.stripeCustomerId;
          await refreshUserData(user.uid); // Update user data with new Stripe customer ID
        } catch (customerError) {
          setError(formatServerError(customerError, 'Failed to create your customer profile. Please try again.'));
          setLoading(false);
          return; // Important: Stop if customer creation fails
        }
      }

      try {
        await attachPaymentMethodToCustomer(stripeCustomerId, paymentMethod.id, user.uid);
        await refreshUserData(user.uid);
        navigate(-1);
      } catch (attachError) {
        setError(formatServerError(attachError, 'We couldn’t attach the card to your profile. Please try again.'));
        setLoading(false);
        return;
      }

    } catch (error) {
      setError(formatStripeError(error));
      console.error('handleAddPaymentMethod error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className='add-payment-backdrop'>
        <div className="container">
          <div className='payment-add-button-container'>
            <WhiteBackButton />
          </div>
          <div className="add-payment-method">
            <h2 className='add-payment-header'>Add Payment Method</h2>
            <form>
              <label htmlFor="cardholderName">Cardholder Name</label>
              <input
                type="text"
                id="cardholderName"
                name="cardholderName"
                value={cardholderName}
                onChange={(e) => setCardholderName(e.target.value)}
                placeholder="Enter Here"
                required // Added required attribute
              />
            </form>
            <div className='add-payment-stripe-container'>
              <label className='add-payment-stripe-label'>Card Details</label>
              <CardElement className='StripeElement' options={cardElementOptions} />
            </div>
            {error && <p className="error-message">{error}</p>}
          </div>
        </div>
      </div>
      <div className='add-payment-method-submit-container'>
        <button onClick={handleAddPaymentMethod} className='add-payment-method-submit' disabled={loading}>
          {loading ? 'Processing...' : 'Add'}
        </button>
      </div>
      <Footer />
    </>
  );
};

export default AddPaymentMethod;
